const Cookies = require('js-cookie');
const {AmpersandApp: productCatalogApp, AmpersandModel} = require('catalog/libs');

const HPELog = require('core/log');
const HPEConstants = require('../../../base/hpe.constants');
const formatProductPrice = require('../../price/hpe.catalog.price.controller').formatProductPrice;

/**
 * Core app model used by product list app
 * handles JSON loading
 */
module.exports = AmpersandModel.extend({
    /**
     * state properties
     */
    props: {
        'isLoading': ['boolean', false, true],
        'isErrorMode': ['boolean', false, false],
        'offsetTop': 'number',
        'minHeight': 'number',
        'navListHeight': 'number',
        'state': 'object',
        'tabLabel': 'string',
        'pageLabel': 'string',
        'oidList': ['array', false, function() { return []; }]
    },

    /**
     * fetch from service URL
     */
    fetch(options) {
        options = options || {};

        //show the loader
        this.isLoading = true;

        //response callbacks + configuration
        options.success = this.onFetchSuccess.bind(this);
        options.error = this.onFetchError.bind(this);

        return AmpersandModel.prototype.fetch.call(this, options);
    },

    /**
     * url for fetch based on app state
     * example service url: "pip.tab-models.oid-67812313.filters-facet_prodtype:Data%20replication,3PAR%20Storage,Data%20cartridges|facet_seriesname:Gen9.page-1.q.json"
     * @return {string} fetch url
     */
    url() {
        var urlString = window.location.protocol + '//' + window.location.host + this.state.path + '/' + this.state.name;
        var filterLength = (this.state.filters) ? this.state.filters.length : 0;
        var count = 1;

        //url selectors must come in a particular order
        urlString += '.tab-' + this.state.tab;
        urlString += '.oid-' + this.state.oid;

        //filters
        if (filterLength) {
            urlString += '.filters-';
            this.state.filters.forEach(function(filter) {
                if (filter.values.length > 0) {
                    urlString += filter.key + ':' + filter.values.join(',');
                    if ((count !== filterLength) && (filterLength !== 1)) {
                        urlString += '|';
                    }
                    count++;
                }
            });
        }

        //final parts of ordered service url string
        urlString += '.page-' + (this.state.page || '1');
        urlString += '.hits-' + (this.state.hits || '12');
        urlString += '.q.json' + ((this.state.queryString) ? '?' + this.state.queryString : '');

        //return created service url unless testing in localhost
        if (productCatalogApp.isLocalhost) {
            return (this.state.page === 2) ? 'http://localhost:1313/api/plp-page2.json' : 'http://localhost:1313/api/plp-page1.json';
        } else {
            return urlString;
        }
    },

    /**
     * handle success response from fetch()
     * @param {object} model reference
     * @param {object} response server response
     */
    onFetchSuccess(model, response) {
        var facetList = [];
        var breadcrumbChildLabel = productCatalogApp.tabInstanceTitle;
        var hasCategories = false;
        var categoryCount = 0;

        //do not proceeed unless a successful response
        if (response && response.header && response.header.status === 'success') {
            //create facet list: "categories" first (if present), then filters
            if (response.facet_counts && response.facet_counts.facets) {
                if (response.categories && response.categories.length) {
                    //category facets
                    facetList.push({
                        facets: response.categories,
                        type: 'category'
                    });
                    breadcrumbChildLabel = (response.categoryAggregated) ? undefined : response.categories[0].facetLabel;
                    categoryCount = response.categories.length;
                    hasCategories = true;
                }

                //filter facets
                facetList.push({
                    facets: response.facet_counts.facets,
                    type: 'filter'
                });
            }

            //assign returned data to child models/collections
            productCatalogApp.i18n = response.i18n;
            productCatalogApp.utilitybar.set(response.pagination);
            productCatalogApp.pagination.set(response.pagination);
            productCatalogApp.productcard.reset(response.items);
            if (facetList.length) {
                productCatalogApp.filters.set({
                    facets: facetList,
                    facetCount: response.facet_counts.facets.length,
                    categoryCount: categoryCount,
                    totalMatches: response.pagination.totalMatches,
                    totalFilters: response.breadcrumbs.length
                });
            }

            //breadcrumbs may be just a list or list + category information
            // productCatalogApp.breadcrumbs.unset('childLabel');
            productCatalogApp.breadcrumbs.set({
                hasCategories: hasCategories,
                childLabel: breadcrumbChildLabel,
                items: response.breadcrumbs
            });

            //update child UI elements
            this.onShowSpecsUpdate();
            this.onShowHitSelectUpdate();
            this.onShowUtilityPaginationUpdate();
            this.onI18NLabelChange();
            this.onProductCardAdded();

            //broadcast view render update event
            productCatalogApp.trigger(HPEConstants.catalog.VIEW_UPDATE_RENDER);
        } else {
            this.onFetchError(model, response);
        }
    },

    /**
     * handle error response from fetch()
     * @param {object} optional model referencer
     * @param {object} optional response server response
     */
    onFetchError(model, response) {
        HPELog.debug('product list model fetch error', response);

        //reset everything
        productCatalogApp.utilitybar.clear();
        productCatalogApp.pagination.clear();
        productCatalogApp.productcard.reset();
        productCatalogApp.breadcrumbs.items.reset();
        productCatalogApp.breadcrumbs.clear();

        //show error
        productCatalogApp.trigger(HPEConstants.catalog.VIEW_ERROR);
    },

    /**
     * i18n labels from service come in as one node
     * distribute values to child nodes as needed
     */
    onI18NLabelChange() {
        //default value for hits per page
        var hitCount = '12';

        if (productCatalogApp.i18n) {
            //pagination
            productCatalogApp.pagination.set({
                labelPagination: productCatalogApp.i18n.I18N_PAGINATION,
                labelPrevious: productCatalogApp.i18n.I18N_PREVIOUS,
                labelNext: productCatalogApp.i18n.I18N_NEXT
            });

            //utility bar
            productCatalogApp.utilitybar.set({
                labelShowSpecs: productCatalogApp.i18n.I18N_SHOW_SPECS,
                labelResults: productCatalogApp.i18n.I18N_RESULTS
            });

            //utility bar hit select. value is contained in i18n result
            productCatalogApp.utilitybar.hitsSelect.set({
                labelView: productCatalogApp.i18n.I18N_VIEW,
                hits: productCatalogApp.i18n.I18N_HIT_SELECT
            });
            hitCount = productCatalogApp.utilitybar.hitsSelect.hitCount ||
                       this.state.hits ||
                       hitCount;

            //Set hit select value
            productCatalogApp.utilitybar.hitsSelect.hits.forEach(function(hit) {
                if (String(hitCount) === String(hit.selectValue)) {
                    hit.isSelected = true;
                }
            });

            //breadcrumbs
            productCatalogApp.breadcrumbs.set({ pageLabel: this.pageLabel });

            //each product card
            productCatalogApp.productcard.models.forEach( function(product) {
                product.labelCompare = productCatalogApp.i18n.I18N_COMPARE;
                product.labelPrice = productCatalogApp.i18n.I18N_PRICE;
                product.labelNew = productCatalogApp.i18n.I18N_NEW;
                product.labelUpdated = productCatalogApp.i18n.I18N_UPDATED;

            });

            //filters
            productCatalogApp.filters.set({
                labelMobileOpen: productCatalogApp.i18n.I18N_FILTER,
                labelMobileClose: productCatalogApp.i18n.I18N_DONE,
                labelClear: productCatalogApp.i18n.I18N_CLEAR_ALL,
                labelResults: productCatalogApp.i18n.I18N_RESULTS
            });

            //each facet
            productCatalogApp.filters.facets.forEach(function(facet) {
                facet.label = (facet.type === 'category') ? productCatalogApp.i18n.I18N_SELECT_A_CATEGORY : productCatalogApp.i18n.I18N_FILTER;
            });

            //tab label for product-listing-tab instances, populated from DOM as part of app init
            productCatalogApp.mainView.model.tabLabel = productCatalogApp.tabInstanceTitle;
        }
    },

    /**
    * handle add event of a new product card model
    * @param  {object} productcard reference to newly added productcard model
    */
    onProductCardAdded: function() {
        function checkForPrice (price) {
           if (price !== undefined && price !== '' && parseInt(price) !== 0) {
                return formatProductPrice(price);
           } else {
                return '0';
           }
        }

        var smbLabel, hhoLabel, hhoShowPrice;
        if (window.hpePriceConfig) {
            smbLabel = window.hpePriceConfig.smbLabel;
            hhoLabel = window.hpePriceConfig.hhoLabel;
            hhoShowPrice = window.hpePriceConfig.hhoPrice; //With Tax
        }
        productCatalogApp.productcard.models.forEach( function(product) {
            product.showSpecs = productCatalogApp.showSpecs;
            product.speclistFirstThird.set(product.specs, {parse: true});
            product.speclistLastThird.set(product.specs, {parse: true});

            if (window.hpePriceConfig && hhoShowPrice !== 'false') {
                product.primaryPrice = checkForPrice(product.price_vat);
                product.secondaryPrice = checkForPrice(product.price);
                product.primaryPriceLabel = hhoLabel;
                product.secondaryPriceLabel = smbLabel;
            } else {
                product.primaryPrice = checkForPrice(product.price);
                product.primaryPriceLabel = smbLabel;
            }

            if (productCatalogApp.tabInstanceTitle === 'Services') {
                product.categoryLabel = product.subcategory;
            } else {
                product.categoryLabel = product.category;
            }
        });

        //update product card compare checkbox state on load
        this.productCardCompareStateUpdate();
    },

    /**
     * manage state of show specs UI (hide on all PDP tabs unless models)
     */
    onShowSpecsUpdate() {
        if (productCatalogApp.isTabInstance) {
            productCatalogApp.showSpecs = true;
            productCatalogApp.utilitybar.specsUIVisible = false;
        } else {
            productCatalogApp.showSpecs = this.getShowSpecsPreference();
            //productCatalogApp.isTabInstance resolves to true or false. The UI is hidden on PLP.
            productCatalogApp.utilitybar.set({
                'specsVisible': productCatalogApp.showSpecs,
                'specsUIVisible': productCatalogApp.isTabInstance
            });
        }
    },

    /**
    * manage state of listing hit selection UI (hide on PLP)
    */
    onShowHitSelectUpdate() {
      if (productCatalogApp.isTabInstance) {
          productCatalogApp.utilitybar.hitsSelect.hitsUIVisible = true;
      } else {
          productCatalogApp.utilitybar.hitsSelect.hitCount = productCatalogApp.getHitCountPreference();
      }
    },

    /**
    * manage state of utility bar paginiagion UI (show on PLP)
    */
    onShowUtilityPaginationUpdate() {
      if (productCatalogApp.isTabInstance) {
          productCatalogApp.utilitybar.paginationUIVisible = true;
      }
    },

    /**
    * update each product card compare checkbox to reflect most recent data state
    * @param {object} oidlist optional array of oids in use
    */
    productCardCompareStateUpdate(oidList) {
        var that = this;

        //update model with passed oidList if exists
        if (typeof oidList !== 'undefined') {
            this.oidList = oidList.slice(0);
        }

        //determine if all compare lists are full
        const compareMaxReached = (this.oidList.length >= HPEConstants.catalog.COMPARE_COUNT_MAX);

        //should the compare button be ticked or should compare buttons be disabled
        productCatalogApp.productcard.models.forEach(function(product) {
            if (that.oidList.indexOf(product.oid) !== -1) {
                product.isCompareChecked = true;
            } else {
                product.isCompareChecked = false;
                product.isCompareDisabled = compareMaxReached;
            }
        });
    },

    /**
     * pre-parse data before handoff to fetch success callback
     * @param {object} response from server
     * @return {object} reformated response
     */
    parse(response) {
        var breadcrumbs, categoryBreadcrumbs = [];
        var smbPrice, hhoPrice;
        var isMarketplaceURL;
        var priceType;

        //pre-parse items to remove "content" object
        if (response && response.result && response.result.items) {
            //check for CTA configuration
            if (window.hpePriceConfig) {
                smbPrice = window.hpePriceConfig.smbPrice;
                hhoPrice = window.hpePriceConfig.hhoPrice;
            }

            response.items = response.result.items.map(function(product) {
                product.specs = (product.content && product.content.specs) ? product.content.specs : [];
                product.images = (product.content && product.content.images) ? product.content.images : [];

                if (!response.result.showCompareCheckbox) {
                  product.compare = false;
                } else if (window.hpeDisableCompare) {
                  product.compare = window.hpeDisableCompare.compareHidden === 'false';
                } else {
                  product.compare = true;
                }

                product.new = (product.new === '1');
                product.updated = (product.update === '1');

                //Lookup CTA messaging by key and set CTA URL
                if (smbPrice === 'true' && product.cta_smb) {
                    priceType = product.cta_smb;
                }

                if (hhoPrice === 'true' && smbPrice !== 'true' && product.cta_hho) {
                    priceType = product.cta_hho;
                }

                if (priceType) {
                    product.cta = priceType.label;
                    product.ctaURL = priceType.url;
                    isMarketplaceURL = priceType.name === 'Marketplace';
                    product.productURL = isMarketplaceURL ? product.ctaURL : product.produrl;
                } else {
                    if (product.produrl) {
                        product.productURL = product.produrl;
                    }
                }

                delete product.content;
                return product;
            });
        }

        //facet pre-parsing
        if (response && response.facet_counts && response.facet_counts.facets) {

            //discard malformed facets or facets missing required values
            response.facet_counts.facets = this.parseFilterValidFacet(response.facet_counts.facets, 'facet');

            //augment each "filtergroup" open/closed expanded/collapsed flag with external saved state
            if (productCatalogApp.filtersExpanded.groupsExpanded) {
                response.facet_counts.facets.forEach(function(filtergroup) {
                    if (typeof productCatalogApp.filtersExpanded.groupsExpanded[filtergroup.facet_id] !== 'undefined') {
                        filtergroup.expanded = productCatalogApp.filtersExpanded.groupsExpanded[filtergroup.facet_id];
                    }
                });
            }

            //facet pre-parsing only for PLP
            if (!productCatalogApp.isTabInstance) {

                //extract "category" data from merged facet data for non-PDP pages
                response.categories = this.parseFilterValidFacet(response.facet_counts.categoryFacets, 'category');

                //create list of breadcrumbs from categories
                categoryBreadcrumbs = this.parseBreadcrumbsFromList(response.categories);
                response.categoryAggregated = this.parseBreadcrumbsAreAnyAggregated(categoryBreadcrumbs);
            }

            //pre-parse filters (and optionally categories) to create list of breadcrumbs
            breadcrumbs = this.parseBreadcrumbsFromList(response.facet_counts.facets);
            response.breadcrumbs = [].concat(categoryBreadcrumbs, breadcrumbs);
        }

        return response;
    },

    /**
     * filter a given a list of facets for valid facets, suplemented with contextual data
     * @param {array} list target list to parse
     * @param {string} facetType value of type of filter (category, facet)
     * @return {array} parse list or empty list
     */
    parseFilterValidFacet: function(list, facetType) {
        return list.filter(function(facet) {

            //facet must have id and at least one value
            if (facet.facet_id && facet.facetValues && facet.facetValues.length > 0) {
                //augment each facet with type and context values
                return facet.facetValues.map(function(filter) {
                    filter.type = facetType;
                    filter.context = (productCatalogApp.isTabInstance) ? 'pdp' : 'plp';
                });
            } else {
                return false;
            }
        });
    },

    /**
     * given a list of facets, return a breadcrumb list
     * @param {array} list target list to parse
     * @return {array} parse list or empty list
     */
    parseBreadcrumbsFromList(list) {
        var breadcrumbList;
        var breadcrumbDuplicateFilter = {};

        if (list && list.length) {
            //only select facets that have been selected
            breadcrumbList = list.map(function(facet) {
                if (facet.facetValues) {
                    return facet.facetValues.filter(function(value) {
                        return (value && value.isChecked) ? value.isChecked : false;
                    })

                    //return usable facet values
                    .map(function(filter) {
                        return {
                            facetValueId: filter.facetValueId,
                            label: filter.facetValue,
                            parentId: facet.facet_id,
                            isCategory: facet.isCategory,
                            parentLabel: facet.facetLabel,
                            context: filter.context
                        };
                    })

                    //guarantee unique entries in the face of duplicate data
                    .filter(function(breadcrumb) {
                        if (!breadcrumbDuplicateFilter[breadcrumb.parentId]) {
                            breadcrumbDuplicateFilter[breadcrumb.parentId] = [];
                        }
                        if (breadcrumbDuplicateFilter[breadcrumb.parentId].indexOf(breadcrumb.facetValueId) === -1) {
                            breadcrumbDuplicateFilter[breadcrumb.parentId].push(breadcrumb.facetValueId);
                            return true;
                        } else {
                            return false;
                        }
                    });
                } else {
                    return [];
                }
            //aggregate facet groups of 3+ by a single facet id instead of multiple children
            }).map(function(breadcrumbList) {
                if (breadcrumbList.length > 2) {
                    return [{
                        id: breadcrumbList[0].parentId,
                        label: breadcrumbList[0].parentLabel,
                        parentId: breadcrumbList[0].parentId,
                        isCategory: breadcrumbList[0].isCategory,
                        context: breadcrumbList[0].context,
                        isAggregated: true,
                        count: breadcrumbList.length.toString()
                    }];
                } else {
                    return breadcrumbList;
                }
            });

            //return consolidated list
            return [].concat.apply([], breadcrumbList);

        } else {
            return [];
        }
    },

    /**
     * given a list of breadcrumbs, return boolean if one of more are aggregated
     * @param {array} list target list to parse
     * @return {boolean} true or false if one or more breadcrumbs is aggregated
     */
    parseBreadcrumbsAreAnyAggregated(list) {
        if (list.length) {
            return list.some(function(breadcrumb) {
                return breadcrumb.isAggregated;
            });
        } else {
            return false;
        }
    },

    /**
     * given a state string/JSON url with selectors, parse values according to
     * product catalog selector rules and return an object with values --
     * cannot use standard hpe.utilities.parseParams here because complex
     * values may include delimiters inside values
     * @return {object} key/value pairs of state selectors
     */
    parseStateString(stateString) {
        // var filters, path, params;
        var path, filters;
        var parsedState = {};

        if (stateString) {
            if (stateString.includes('?')) {
                parsedState.queryString = stateString.split('?')[1];
            }

            //find requested page or default to page 1
            parsedState.page = this.extractSelectorFromString(stateString, 'page', '-', '1');

            //extract requested filters or default to empty list
            filters = this.extractSelectorFromString(stateString, 'filters', 'filters-', []);
            parsedState.filters = this.parseListSelector(filters);

            //specific to PDP/tab instance
            if (productCatalogApp.isTabInstance) {

                parsedState.tab = this.extractSelectorFromString(stateString, 'tab', '-', '');
                parsedState.oid = this.extractSelectorFromString(stateString, 'oid', '-', '');

                //page name ("product", "pip", etc)
                path = stateString.split('/');
                parsedState.name = path.pop().split('.')[0];

                //base path for XHR requests
                parsedState.path = path.join('/');
            }
        }

        return parsedState;
    },

    /**
     * given a selector value, return a list of parsed values where each list
     * item is an object with parsed key/value entries
     * @param  {string} selector value to be parsed
     * @return {array} list of parsed values or empty list
     */
    parseListSelector: function(selector) {
        if (selector && selector.length) {
            return selector.split('|').map(function(value) {
                var selectorKV = value.split(':');
                return {
                    key: selectorKV[0],
                    values: selectorKV[1].split(',')
                };
            });
        } else {
            return [];
        }
    },

    /**
     * given a state string/JSON url with selectors, return extracted selector sub-string or given default
     * @param {string} stateString string to parse
     * @param {string} selector string key to extract
     * @param {string} delimier string to split on
     * @param {any} defaultValue default value associated with selector in case of no match
     * @return {string} extracted selector string or empty string
     */
    extractSelectorFromString: function(stateString, selector, delimiter, defaultValue) {
        var params = stateString.split('.');
        return params.reduce(function(value, param) {
            if (param.indexOf(selector) !== -1) {
                return value = param.split(delimiter)[1];
            } else {
                return value;
            }
        }, defaultValue);
    },

    /**
     * add a filter to model state
     * @param  {object} params descriptor of filter to add
     */
    filterAdd(params) {
        var facetGroupExists = false;

        //add filter to existing facet if it exists
        this.state.filters.filter(function(facet) {
            return facet.key === params.id;
        }).map(function(facet) {
            facet.values.push(params.value);
            facetGroupExists = true;
        });

        //create new facet with filter value
        if (!facetGroupExists) {
            this.state.filters.push({
                key: params.id,
                values: [params.value]
            });
        }

        //always reset to page 1 when making filter request
        this.state.page = '1';
    },

    /**
     * remove a filter from model state
     * @param  {object} params descriptor of filter to add
     */
    filterRemove(params) {
        var that = this;

        this.state.filters.forEach(function(filter, filterIndex) {
            var index;
            if (filter.key === params.id) {
                index = filter.values.indexOf(params.value);
                if (index !== -1) {
                    filter.values.splice(index, 1);
                }
            }

            //if facet no longer has any filter values, remove facet
            if (!filter.values.length) {
                that.state.filters.splice(filterIndex, 1);
            }
        });

        //always reset to page 1 when making filter request
        this.state.page = '1';
    },

    /**
     * remove all filters from a given facet
     * @param  {object} params descriptor of filter to add
     */
    filterRemoveFacet(params) {
        var i, facetIndex;

        //find the index in the state list of filters matching the facet to remove
        for (i = 0; i < this.state.filters.length; i++) {
            if (this.state.filters[i].key === params.id) {
                facetIndex = i;
                break;
            }
        }

        //remove the facet at specified index
        if (typeof facetIndex !== 'undefined') {
            this.state.filters.splice(facetIndex, 1);
        }

        //always reset to page 1 when making filter request
        this.state.page = '1';
    },

    /**
     * return value of session cookie to indicate if user wants to see specifications
     * if no user preference specified, UI should show specs
     * @return {boolean} true to show specs, false to not show
     */
    getShowSpecsPreference() {
        var showSpecsCookie = Cookies.get(HPEConstants.catalog.SHOW_SPECS_COOKIE);
        return (showSpecsCookie !== 'false');
    }
});
